import { ImageMaps, ParsedCode } from "./selectors";
import { atomFamily, selector } from "recoil";

import { PageIndex } from "./page";

export interface ImageStatus {
  [key: string]: string | boolean;
  userDefinedName: string;
  url: string;
  loaded: boolean;
}

export interface ImageLoadingStatusKey {
  [key: string]: string | boolean;
  userDefinedName: string;
  url: string;
  loaded: boolean;
}

const MAX_CONCURRENT_IMAGE_LOADS = 4;
let imageLoadsInFlight = 0;
const imageLoadQueue: Array<() => void> = [];

function enqueueImageLoad<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const run = () => {
      imageLoadsInFlight++;
      fn()
        .then(resolve)
        .catch(reject)
        .finally(() => {
          imageLoadsInFlight--;
          const next = imageLoadQueue.shift();
          if (next) next();
        });
    };
    if (imageLoadsInFlight < MAX_CONCURRENT_IMAGE_LOADS) {
      run();
    } else {
      imageLoadQueue.push(run);
    }
  });
}

const preloadImage = async (url: string) =>
  enqueueImageLoad(async () => {
    const img = new Image();
    img.src = url;
    await img.decode();
    return true;
  });

export const ImageLoadingStatuses = atomFamily<ImageStatus, ImageLoadingStatusKey>({
  key: "VS_ImageStatuses",
  effects: ({ userDefinedName, url }) => [({ setSelf }) =>
    setSelf(
      preloadImage(url).then(() => {
        return { userDefinedName, url, loaded: true };
      }).catch((err) => {
        console.error(err);
        return { userDefinedName, url, loaded: false };
      }),
    )],
});

// load images in pages about to appear.
export const ImagePreloader = selector({
  key: "VS_ImagePreloader",
  get: ({ get }) => {
    const parsedCode = get(ParsedCode);
    const pageIdx = get(PageIndex);
    const imageMaps = get(ImageMaps);
    const startPage = ((pageIdx - 3) > 0) ? pageIdx - 3 : 0;
    const endPage = pageIdx + 3 > parsedCode.length - 1 ? parsedCode.length - 1 : pageIdx + 3;
    const images: string[] = [];
    for (let i = startPage; i <= endPage; i++) {
      const page = parsedCode[i];
      for (const el of page) {
        if (el && (el.type === "image:" || el.type === "background:")) {
          const map = imageMaps.find(map => map.userDefinedName === el.content);
          if (map) {
            get(ImageLoadingStatuses({ userDefinedName: map.userDefinedName, url: map.url, loaded: true }));
            get(ImageLoadingStatuses({ userDefinedName: map.userDefinedName, url: map.url, loaded: false }));
            images.push(map.userDefinedName);
          }
        }
      }
    }
    return images;
  },
});



